import React, { useEffect, useState, Children, Fragment } from 'react';
import { motion } from 'framer-motion';
// Time-lapse construction of a modern 3-bedroom terrace duplex (a row of 3
// attached contemporary units). Loops every CYCLE_MS via key remount.
const CYCLE_MS = 16000;
export function HeroSkyline() {
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCycle(c => c + 1), CYCLE_MS);
    return () => clearInterval(t);
  }, []);
  return <div aria-hidden className="absolute inset-0 pointer-events-none select-none overflow-hidden opacity-10" data-id="element-428">
      {/* Sun glow */}
      <motion.div animate={{
      y: [0, -6, 0]
    }} transition={{
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute right-[14%] top-[8%] w-32 h-32 rounded-full bg-gold/30 blur-2xl" data-id="element-429" />

      {/* Drifting clouds */}
      <motion.div initial={{
      x: '-15%'
    }} animate={{
      x: '115%'
    }} transition={{
      duration: 60,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute top-[10%] left-0 w-44 h-10 bg-ink/[0.05] rounded-full blur-md" data-id="element-430" />
      <motion.div initial={{
      x: '0%'
    }} animate={{
      x: '120%'
    }} transition={{
      duration: 80,
      repeat: Infinity,
      ease: 'linear',
      delay: 14
    }} className="absolute top-[20%] left-[-15%] w-56 h-12 bg-ink/[0.04] rounded-full blur-md" data-id="element-431" />

      <Construction key={cycle} data-id="element-432" />

      {/* Subtle top bone fade — only covers the nav band, lets the building reach up */}
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-bone to-transparent" data-id="element-433" />
    </div>;
}
/**
 * Modern 3-bedroom terrace duplex — a row of 3 attached units.
 * Coordinates: terrace spans x = 480 → 1120 (640 wide, 3 units of ~213 wide each).
 *   Unit 1: 480–693
 *   Unit 2: 693–906
 *   Unit 3: 906–1120
 * Ground floor: y 380–500 (120h) — garage + entry + tall side window
 * First floor:  y 240–380 (140h) — 3 bedrooms (1 master with balcony per unit)
 */
function Construction() {
  const units = [{
    x0: 480,
    x1: 693
  }, {
    x0: 693,
    x1: 906
  }, {
    x0: 906,
    x1: 1120
  }];
  return <svg viewBox="0 0 1600 600" preserveAspectRatio="xMidYMax slice" className="absolute inset-x-0 bottom-0 w-full h-full" data-id="element-434">
      <defs data-id="element-435">
        <linearGradient id="window-gold" x1="0" y1="0" x2="0" y2="1" data-id="element-436">
          <stop offset="0%" stopColor="#F5B800" stopOpacity="1" data-id="element-437" />
          <stop offset="100%" stopColor="#F5B800" stopOpacity="0.55" data-id="element-438" />
        </linearGradient>
        <linearGradient id="wall-fill" x1="0" y1="0" x2="0" y2="1" data-id="element-439">
          <stop offset="0%" stopColor="#FAF6EC" stopOpacity="1" data-id="element-440" />
          <stop offset="100%" stopColor="#E8DFCB" stopOpacity="1" data-id="element-441" />
        </linearGradient>
        <linearGradient id="accent-fill" x1="0" y1="0" x2="0" y2="1" data-id="element-442">
          <stop offset="0%" stopColor="#0F0F0F" stopOpacity="0.92" data-id="element-443" />
          <stop offset="100%" stopColor="#0F0F0F" stopOpacity="0.78" data-id="element-444" />
        </linearGradient>
        <linearGradient id="ground-fade" x1="0" y1="0" x2="0" y2="1" data-id="element-445">
          <stop offset="0%" stopColor="#0F0F0F" stopOpacity="0.06" data-id="element-446" />
          <stop offset="100%" stopColor="#0F0F0F" stopOpacity="0.14" data-id="element-447" />
        </linearGradient>
      </defs>

      {/* Ground baseline */}
      <rect x="0" y="500" width="1600" height="100" fill="url(#ground-fade)" data-id="element-448" />
      <motion.line x1="0" y1="500" x2="1600" y2="500" stroke="#0F0F0F" strokeOpacity="0.18" strokeWidth="1" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} data-id="element-449" />

      {/* === SURVEYING (0–1.2s) === */}
      <motion.g stroke="#F5B800" strokeWidth="2" strokeDasharray="6 6" fill="none" initial={{
      opacity: 0
    }} animate={{
      opacity: [0, 1, 1, 0]
    }} transition={{
      duration: 2.4,
      times: [0, 0.2, 0.8, 1]
    }} data-id="element-450">
        <line x1="480" y1="500" x2="1120" y2="500" data-id="element-451" />
        <line x1="480" y1="500" x2="480" y2="475" data-id="element-452" />
        <line x1="1120" y1="500" x2="1120" y2="475" data-id="element-453" />
        <line x1="693" y1="500" x2="693" y2="480" data-id="element-454" />
        <line x1="906" y1="500" x2="906" y2="480" data-id="element-455" />
      </motion.g>

      {/* === FOUNDATION SLAB (1–2.5s) === */}
      <motion.rect x="475" y="490" width="650" height="14" fill="#0F0F0F" fillOpacity="0.85" initial={{
      scaleX: 0
    }} animate={{
      scaleX: 1
    }} transition={{
      duration: 1.2,
      delay: 1,
      ease: 'easeOut'
    }} style={{
      originX: '475px'
    }} data-id="element-456" />

      {/* === GROUND FLOOR WALLS — one block, looks like the row of 3 (2.5–4.5s) === */}
      <motion.rect x="480" y="380" width="640" height="110" fill="url(#wall-fill)" stroke="#0F0F0F" strokeOpacity="0.45" strokeWidth="2" initial={{
      scaleY: 0
    }} animate={{
      scaleY: 1
    }} transition={{
      duration: 1.4,
      delay: 2.4,
      ease: 'easeOut'
    }} style={{
      originY: '490px'
    }} data-id="element-457" />

      {/* Vertical separators between units (drawn with the walls) */}
      {[693, 906].map((x, i) => <motion.line key={`sep-g-${i}`} x1={x} y1="380" x2={x} y2="490" stroke="#0F0F0F" strokeOpacity="0.35" strokeWidth="2" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 0.6,
      delay: 3.6 + i * 0.15
    }} data-id="element-458" />)}

      {/* === GROUND FLOOR DOORS, GARAGES, WINDOWS (4.5–6s) === */}
      {units.map((u, i) => {
      const w = u.x1 - u.x0;
      const garageX = u.x0 + 16;
      const garageW = w * 0.42;
      const doorX = u.x0 + w * 0.55;
      const doorW = w * 0.16;
      const winX = u.x0 + w * 0.74;
      const winW = w * 0.22;
      return <motion.g key={`gf-${i}`} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 4.5 + i * 0.18
      }} data-id="element-459">
            {/* Garage door (modern slatted look) */}
            <rect x={garageX} y="402" width={garageW} height="86" fill="url(#accent-fill)" data-id="element-460" />
            {[0, 1, 2, 3, 4].map(row => <line key={row} x1={garageX} y1={418 + row * 16} x2={garageX + garageW} y2={418 + row * 16} stroke="#F5B800" strokeOpacity="0.25" strokeWidth="1" data-id="element-461" />)}
            {/* Front door */}
            <rect x={doorX} y="412" width={doorW} height="76" fill="url(#accent-fill)" data-id="element-462" />
            <circle cx={doorX + doorW - 4} cy="452" r="1.6" fill="#F5B800" data-id="element-463" />
            {/* Side window */}
            <rect x={winX} y="408" width={winW} height="50" fill="#0F0F0F" fillOpacity="0.22" stroke="#0F0F0F" strokeOpacity="0.55" strokeWidth="1.5" data-id="element-464" />
            <line x1={winX + winW / 2} y1="408" x2={winX + winW / 2} y2="458" stroke="#0F0F0F" strokeOpacity="0.45" strokeWidth="1" data-id="element-465" />
          </motion.g>;
    })}

      {/* === FIRST FLOOR SLAB + WALLS (6–7.5s) === */}
      <motion.line x1="480" y1="380" x2="1120" y2="380" stroke="#0F0F0F" strokeOpacity="0.55" strokeWidth="3" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 0.8,
      delay: 6
    }} data-id="element-466" />
      <motion.rect x="480" y="240" width="640" height="140" fill="url(#wall-fill)" stroke="#0F0F0F" strokeOpacity="0.45" strokeWidth="2" initial={{
      scaleY: 0
    }} animate={{
      scaleY: 1
    }} transition={{
      duration: 1.4,
      delay: 6.4,
      ease: 'easeOut'
    }} style={{
      originY: '380px'
    }} data-id="element-467" />
      {[693, 906].map((x, i) => <motion.line key={`sep-f-${i}`} x1={x} y1="240" x2={x} y2="380" stroke="#0F0F0F" strokeOpacity="0.35" strokeWidth="2" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 0.6,
      delay: 7.4 + i * 0.15
    }} data-id="element-468" />)}

      {/* === FIRST FLOOR — bedrooms + master balcony (7.5–9s) === */}
      {units.map((u, i) => {
      const w = u.x1 - u.x0;
      // Master with balcony (left), bedroom 2 (middle small), bedroom 3 (right tall window)
      const masterX = u.x0 + 12;
      const masterW = w * 0.48;
      const bed2X = u.x0 + w * 0.58;
      const bed2W = w * 0.18;
      const bed3X = u.x0 + w * 0.8;
      const bed3W = w * 0.16;
      return <motion.g key={`ff-${i}`} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 7.6 + i * 0.18
      }} data-id="element-469">
            {/* Master sliding glass door */}
            <rect x={masterX} y="262" width={masterW} height="90" fill="#0F0F0F" fillOpacity="0.22" stroke="#0F0F0F" strokeOpacity="0.55" strokeWidth="1.5" data-id="element-470" />
            {[1, 2].map(s => <line key={s} x1={masterX + masterW * s / 3} y1="262" x2={masterX + masterW * s / 3} y2="352" stroke="#0F0F0F" strokeOpacity="0.45" strokeWidth="1" data-id="element-471" />)}
            {/* Balcony rail */}
            <line x1={masterX - 4} y1="364" x2={masterX + masterW + 4} y2="364" stroke="#0F0F0F" strokeOpacity="0.7" strokeWidth="2" data-id="element-472" />
            {[...Array(7)].map((_, k) => <line key={k} x1={masterX + masterW * k / 6} y1="352" x2={masterX + masterW * k / 6} y2="378" stroke="#0F0F0F" strokeOpacity="0.55" strokeWidth="1" data-id="element-473" />)}
            {/* Bedroom 2 window */}
            <rect x={bed2X} y="270" width={bed2W} height="60" fill="#0F0F0F" fillOpacity="0.22" stroke="#0F0F0F" strokeOpacity="0.55" strokeWidth="1.5" data-id="element-474" />
            <line x1={bed2X + bed2W / 2} y1="270" x2={bed2X + bed2W / 2} y2="330" stroke="#0F0F0F" strokeOpacity="0.45" strokeWidth="1" data-id="element-475" />
            {/* Bedroom 3 — tall narrow accent window */}
            <rect x={bed3X} y="262" width={bed3W} height="90" fill="#0F0F0F" fillOpacity="0.22" stroke="#0F0F0F" strokeOpacity="0.55" strokeWidth="1.5" data-id="element-476" />
          </motion.g>;
    })}

      {/* === ROOF — flat with parapet + slim ink coping (9–10.5s) === */}
      <motion.rect x="475" y="220" width="650" height="22" fill="url(#wall-fill)" stroke="#0F0F0F" strokeOpacity="0.45" strokeWidth="2" initial={{
      scaleY: 0
    }} animate={{
      scaleY: 1
    }} transition={{
      duration: 0.7,
      delay: 9,
      ease: 'easeOut'
    }} style={{
      originY: '242px'
    }} data-id="element-477" />
      {/* Coping strip */}
      <motion.rect x="475" y="218" width="650" height="6" fill="#0F0F0F" fillOpacity="0.85" initial={{
      scaleX: 0
    }} animate={{
      scaleX: 1
    }} transition={{
      duration: 0.8,
      delay: 9.6,
      ease: 'easeOut'
    }} style={{
      originX: '475px'
    }} data-id="element-478" />
      {/* Gold accent line under coping */}
      <motion.line x1="475" y1="226" x2="1125" y2="226" stroke="#F5B800" strokeOpacity="0.85" strokeWidth="2" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 0.8,
      delay: 10
    }} data-id="element-479" />

      {/* === LIGHTS ON (11–12.5s) === */}
      <motion.g fill="url(#window-gold)" initial="hidden" animate="show" variants={{
      hidden: {},
      show: {
        transition: {
          staggerChildren: 0.07,
          delayChildren: 11
        }
      }
    }} data-id="element-480">
        {units.map((u, i) => {
        const w = u.x1 - u.x0;
        // Side window ground floor
        const winX = u.x0 + w * 0.74;
        const winW = w * 0.22;
        // First floor master + bed3 (master glass + bed3)
        const masterX = u.x0 + 12;
        const masterW = w * 0.48;
        const bed3X = u.x0 + w * 0.8;
        const bed3W = w * 0.16;
        return <Fragment key={`lit-${i}`} data-id="element-481">
              {/* Ground side window glow */}
              <motion.rect x={winX + 2} y={410} width={winW - 4} height={46} variants={{
            hidden: {
              opacity: 0,
              scale: 0.5
            },
            show: {
              opacity: [0, 1, 0.85, 1],
              scale: 1
            }
          }} transition={{
            duration: 0.9
          }} data-id="element-482" />
              {/* Master sliding glass glow */}
              <motion.rect x={masterX + 2} y={264} width={masterW - 4} height={86} variants={{
            hidden: {
              opacity: 0,
              scale: 0.5
            },
            show: {
              opacity: [0, 1, 0.85, 1],
              scale: 1
            }
          }} transition={{
            duration: 0.9
          }} data-id="element-483" />
              {/* Bed3 narrow glow */}
              <motion.rect x={bed3X + 2} y={264} width={bed3W - 4} height={86} variants={{
            hidden: {
              opacity: 0,
              scale: 0.5
            },
            show: {
              opacity: [0, 1, 0.85, 1],
              scale: 1
            }
          }} transition={{
            duration: 0.9
          }} data-id="element-484" />
            </Fragment>;
      })}
      </motion.g>

      {/* === LANDSCAPING (12.5–14s) === */}
      {/* Trees on either end */}
      {[{
      x: 430,
      scale: 1
    }, {
      x: 1170,
      scale: 1.05
    }, {
      x: 400,
      scale: 0.85
    }, {
      x: 1200,
      scale: 0.9
    }].map((tree, i) => <motion.g key={`tree-${i}`} initial={{
      opacity: 0,
      y: 12,
      scale: 0.6
    }} animate={{
      opacity: 1,
      y: 0,
      scale: tree.scale
    }} transition={{
      duration: 0.6,
      delay: 12.5 + i * 0.15,
      ease: 'backOut'
    }} style={{
      transformOrigin: `${tree.x}px 500px`
    }} data-id="element-485">
          <rect x={tree.x - 3} y="475" width="6" height="25" fill="#0F0F0F" fillOpacity="0.7" data-id="element-486" />
          <circle cx={tree.x} cy="465" r="18" fill="#0F0F0F" fillOpacity="0.55" data-id="element-487" />
          <circle cx={tree.x - 8} cy="475" r="14" fill="#0F0F0F" fillOpacity="0.55" data-id="element-488" />
          <circle cx={tree.x + 8} cy="475" r="14" fill="#0F0F0F" fillOpacity="0.55" data-id="element-489" />
        </motion.g>)}

      {/* Driveways (one per unit, in front of garage) */}
      {units.map((u, i) => {
      const w = u.x1 - u.x0;
      const dx = u.x0 + 16;
      const dw = w * 0.42;
      return <motion.path key={`drive-${i}`} d={`M ${dx} 490 L ${dx - 18} 540 L ${dx + dw + 18} 540 L ${dx + dw} 490 Z`} fill="#0F0F0F" fillOpacity="0.22" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 13 + i * 0.1
      }} data-id="element-490" />;
    })}

      {/* Construction crane (visible early, fades out at end) */}
      <motion.g stroke="#F5B800" strokeWidth="2.5" fill="none" initial={{
      opacity: 0
    }} animate={{
      opacity: [0, 1, 1, 1, 0]
    }} transition={{
      duration: 14,
      times: [0, 0.1, 0.5, 0.85, 0.95]
    }} data-id="element-491">
        <line x1="1180" y1="500" x2="1180" y2="160" data-id="element-492" />
        <line x1="1180" y1="160" x2="1320" y2="160" data-id="element-493" />
        <line x1="1180" y1="180" x2="1300" y2="160" data-id="element-494" />
        <line x1="1180" y1="160" x2="1120" y2="160" data-id="element-495" />
        <line x1="1180" y1="180" x2="1130" y2="160" data-id="element-496" />
        <motion.g animate={{
        x: [0, -30, 0, 30, 0]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-497">
          <line x1="1260" y1="160" x2="1260" y2="280" data-id="element-498" />
          <rect x="1252" y="280" width="16" height="10" fill="#F5B800" stroke="none" data-id="element-499" />
        </motion.g>
        <rect x="1170" y="160" width="20" height="14" fill="#F5B800" stroke="none" data-id="element-500" />
      </motion.g>
    </svg>;
}